/**
 * Словарь auth-ошибок (синхронизирован с `pwa/lib/auth-errors.ts`).
 */

const DICT: Record<string, string> = {
  'Invalid email or password': 'Неверный email или пароль.',
  'Account is deactivated': 'Учётная запись отключена. Обратитесь к администратору.',
  password_too_short: 'Пароль должен быть не короче 10 символов.',
  password_too_long: 'Пароль не должен быть длиннее 128 символов.',
  password_missing_letter: 'Добавьте в пароль хотя бы одну букву.',
  password_missing_digit: 'Добавьте в пароль хотя бы одну цифру.',
  password_has_whitespace_edges: 'Уберите пробелы в начале или в конце пароля.',
  privacy_policy_required: 'Нужно согласие с политикой конфиденциальности.',
  privacy_policy_version_mismatch:
    'Политика конфиденциальности обновилась. Перезагрузите страницу и подтвердите согласие ещё раз.',
  email_not_verified: 'Сначала подтвердите email кодом из письма.',
  email_delivery_not_configured:
    'На сервере не настроена отправка почты. Обратитесь к администратору.',
  invalid_token: 'Ссылка недействительна или была введена с опечаткой.',
  token_already_used: 'Эта ссылка уже была использована. Запросите новое письмо.',
  token_expired: 'Срок действия ссылки истёк (30 минут). Запросите новое письмо.',
  account_inactive: 'Учётная запись отключена. Обратитесь к администратору.',
  invalid_credentials: 'Неверный email или пароль.',
};

const FALLBACK = 'Произошла ошибка. Попробуйте ещё раз.';

const CYRILLIC_RE = /[А-Яа-яЁё]/;

/** FastAPI handler returns `{ error, status_code }`; normalize to a single detail payload. */
export function resolveApiErrorDetail(errorData: Record<string, unknown> | undefined): unknown {
  if (!errorData) return undefined;
  if ('detail' in errorData && errorData.detail != null) return errorData.detail;
  if ('error' in errorData && errorData.error != null) return errorData.error;
  return undefined;
}

export type AuthThrottleMeta = {
  retryAfterSeconds?: number;
  attemptsRemaining?: number;
};

export type AuthFormAlert = {
  message: string;
  hint?: string;
  hintTone?: 'warning' | 'muted';
};

function parsePositiveInt(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function extractAuthThrottleMeta(
  response: Response,
  errorData: Record<string, unknown> | undefined
): AuthThrottleMeta {
  const meta: AuthThrottleMeta = {};
  const detail = resolveApiErrorDetail(errorData);

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const d = detail as Record<string, unknown>;
    if (d.code === 'too_many_attempts' && typeof d.retry_after_seconds === 'number') {
      meta.retryAfterSeconds = Math.max(1, Math.floor(d.retry_after_seconds));
    }
  }

  const headerRetry = parsePositiveInt(response.headers.get('Retry-After'));
  if (!meta.retryAfterSeconds && headerRetry) {
    meta.retryAfterSeconds = headerRetry;
  }

  const headerRemaining = parsePositiveInt(response.headers.get('X-Auth-Attempts-Remaining'));
  if (headerRemaining) {
    meta.attemptsRemaining = headerRemaining;
  }

  return meta;
}

export function formatAuthThrottleWait(seconds: number): string {
  const sec = Math.max(1, Math.ceil(seconds));
  if (sec >= 24 * 60 * 60) {
    const days = Math.ceil(sec / (24 * 60 * 60));
    return days === 1 ? '1 сутки' : `${days} суток`;
  }
  if (sec >= 60 * 60) {
    const hours = Math.ceil(sec / (60 * 60));
    return hours === 1 ? '1 час' : `${hours} ч`;
  }
  const minutes = Math.ceil(sec / 60);
  if (minutes === 1) return '1 минуту';
  if (minutes >= 2 && minutes <= 4) return `${minutes} минуты`;
  return `${minutes} минут`;
}

function fromString(detail: string, fallback: string): string {
  if (DICT[detail]) return DICT[detail];
  if (CYRILLIC_RE.test(detail)) return detail;
  if (/^value is not a valid /i.test(detail) || /^field required$/i.test(detail.trim())) {
    return fallback;
  }
  return fallback;
}

function translateDetailObject(detail: Record<string, unknown>, fallback: string): string | null {
  if (detail.code === 'too_many_attempts') {
    return 'Слишком много попыток';
  }
  return null;
}

export function translateAuthError(detail: unknown, fallback: string = FALLBACK): string {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return fromString(detail, fallback);
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first && typeof first === 'object') {
      const msg = (first as { msg?: unknown }).msg;
      if (typeof msg === 'string') return fromString(msg, fallback);
    }
    if (typeof first === 'string') return fromString(first, fallback);
    return fallback;
  }
  if (typeof detail === 'object') {
    const structured = translateDetailObject(detail as Record<string, unknown>, fallback);
    if (structured) return structured;
    const msg = (detail as { msg?: unknown; detail?: unknown }).msg;
    if (typeof msg === 'string') return fromString(msg, fallback);
    const inner = (detail as { detail?: unknown }).detail;
    if (typeof inner === 'string') return fromString(inner, fallback);
  }
  return fallback;
}

function readRetryAfterSecondsFromDetail(detail: unknown): number | undefined {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return undefined;
  const sec = (detail as { retry_after_seconds?: unknown }).retry_after_seconds;
  if (typeof sec === 'number' && Number.isFinite(sec) && sec > 0) {
    return Math.max(1, Math.floor(sec));
  }
  return undefined;
}

function extractMetaFromApiError(err: unknown): { meta: AuthThrottleMeta; detail: unknown } {
  const meta: AuthThrottleMeta = {};
  let detail: unknown;
  if (err && typeof err === 'object') {
    const data = (err as { data?: Record<string, unknown> }).data;
    if (data) {
      if (typeof data.retryAfterSeconds === 'number') {
        meta.retryAfterSeconds = data.retryAfterSeconds;
      } else {
        const fromDetail = readRetryAfterSecondsFromDetail(resolveApiErrorDetail(data));
        if (fromDetail) meta.retryAfterSeconds = fromDetail;
      }
      if (typeof data.attemptsRemaining === 'number') {
        meta.attemptsRemaining = data.attemptsRemaining;
      }
      detail = resolveApiErrorDetail(data);
    }
    if (detail === undefined) {
      detail = (err as { message?: unknown }).message;
    }
  }
  return { meta, detail };
}

export function parseAuthFormAlert(err: unknown, fallback: string = FALLBACK): AuthFormAlert {
  const { meta, detail } = extractMetaFromApiError(err);

  if (meta.retryAfterSeconds) {
    const wait = formatAuthThrottleWait(meta.retryAfterSeconds);
    return {
      message: 'Слишком много попыток',
      hint: `Подождите ${wait} и повторите.`,
      hintTone: 'muted',
    };
  }

  const message =
    detail !== undefined && detail !== null
      ? translateAuthError(detail, fallback)
      : fallback;

  if (meta.attemptsRemaining === 1) {
    return {
      message,
      hint: 'Осталась 1 попытка — затем пауза 15 минут.',
      hintTone: 'warning',
    };
  }

  return { message };
}

export function translateAuthApiError(err: unknown, fallback: string = FALLBACK): string {
  return parseAuthFormAlert(err, fallback).message;
}
